import { gql } from '@apollo/client';
import type { AlertBannerStatus } from '@apollo/orbit';
import { AlertBanner, Link, Text } from '@apollo/orbit';
import React from 'react';

import type { EOLBannerFragment } from 'src/lib/graphqlTypes/types';
import { BillingPlanKind } from 'src/lib/graphqlTypes/types';

export const eolBannerFragment = gql<EOLBannerFragment, Record<string, never>>`
  fragment EOLBannerFragment on BillingSubscription {
    uuid
    plan {
      id
      kind
    }
  }
`;

const Banner = ({
  title,
  status,
  message,
}: {
  title: string;
  status: AlertBannerStatus;
  message: React.ReactNode;
}) => {
  return (
    <AlertBanner status={status}>
      <div>
        <Text variant="bodySb">{title}</Text>
        <Text>{message}</Text>
      </div>
    </AlertBanner>
  );
};

export const EOLBanner = ({
  subscription,
}: {
  subscription: EOLBannerFragment | null | undefined;
}) => {
  if (!subscription) return null;

  switch (subscription?.plan?.kind) {
    case BillingPlanKind.COMMUNITY:
      return (
        <Banner
          title="Action Required! Your Apollo plan is being discontinued."
          status="error"
          message={
            <>
              Your current Legacy Free plan will be retired on{' '}
              <b>February 1, 2026</b>. Switch to a new plan today to keep using
              Apollo without interruption. For more information,{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://support.apollographql.com/space/ETKB/2022146049/Legacy+Free+Plan:+End-of-Life+FAQ"
              >
                refer to our FAQ
              </Link>
              .
            </>
          }
        />
      );
    case BillingPlanKind.SERVERLESS_FREE:
    case BillingPlanKind.SERVERLESS_PAID:
    case BillingPlanKind.SERVERLESS:
      return (
        <Banner
          title="Action Required! Your Apollo plan is being discontinued."
          status="error"
          message={
            <>
              Your current Serverless plan will be retired on{' '}
              <b>February 1, 2026</b>. Good news – you can{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://www.apollographql.com/docs/graphos/routing/cloud/migrate-to-self-hosted"
              >
                migrate your graphs
              </Link>{' '}
              and keep using Apollo without interruption. For more information,{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://support.apollographql.com/space/ETKB/2022047765/Serverless+&+Dedicated+Plans:+End-of-Life+FAQ"
              >
                refer to our FAQ
              </Link>
              .
            </>
          }
        />
      );
    case BillingPlanKind.TEAM_PAID:
      return (
        <Banner
          title="We have new and improved Apollo plans"
          status="warning"
          message={
            <>
              Switch today to our new usage-based Developer plan, or contact our
              team to learn about Standard. For more information,{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://support.apollographql.com/space/ETKB/2007662603/Teams+Plan:+End-of-Life+FAQ"
              >
                refer to our FAQ
              </Link>
              .
            </>
          }
        />
      );
    case BillingPlanKind.DEDICATED:
      return (
        <Banner
          title="Action Required! Your Apollo plan is being discontinued."
          status="warning"
          message={
            <>
              Your current Dedicated plan will retire on <b>March 15, 2026</b>.
              Good news – you can{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://www.apollographql.com/docs/graphos/routing/cloud/migrate-to-self-hosted"
              >
                migrate your graphs
              </Link>{' '}
              and keep using Apollo without interruption. For more information,{' '}
              <Link
                className="whitespace-nowrap"
                isExternal
                to="https://support.apollographql.com/space/ETKB/2022047765/Serverless+&+Dedicated+Plans:+End-of-Life+FAQ"
              >
                refer to our FAQ
              </Link>
              .
            </>
          }
        />
      );
    default:
      return null;
  }
};
